import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { get, post, del } from '../../lib/request'

function normalizeExemplarList(res) {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res?.data?.data)) return res.data.data
  return []
}

/** GET /api/v1/exemplar-post/:clientId */
function exemplarClientIdFromArg(arg) {
  return arg && typeof arg === "object" && !Array.isArray(arg) ? arg.clientId : arg
}

export const fetchExemplarPostsByClientId = createAsyncThunk(
  'exemplarPosts/fetchByClient',
  async (arg, { rejectWithValue }) => {
    const clientId = exemplarClientIdFromArg(arg)
    try {
      const data = await get(`exemplar-posts/clients/${clientId}`)
      return { clientId, list: normalizeExemplarList(data) }
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load exemplar posts')
    }
  },
  {
    condition: (arg, { getState }) => {
      const force = arg && typeof arg === "object" && !Array.isArray(arg) ? arg.force : false
      const clientId = exemplarClientIdFromArg(arg)
      if (force) return true
      if (clientId == null || clientId === "") return false
      const slice = getState().exemplarPosts ?? {}
      const same = String(slice.clientId ?? "") === String(clientId)
      if (slice.status === "loading" && same) return false
      if (slice.status === "succeeded" && same) return false
      return true
    },
  },
)

/**
 * POST /api/v1/exemplar-post/:clientId
 * Body: { socialNetworkTypeId, postUrl, postText } — optional `id` if backend supports update via POST.
 */
export const saveExemplarPost = createAsyncThunk(
  'exemplarPosts/save',
  async ({ clientId, socialNetworkTypeId, postUrl, postText, id }, { rejectWithValue }) => {
    try {
      const body = { socialNetworkTypeId, postUrl, postText }
      if (id != null) body.id = id
      const data = await post(`exemplar-posts/clients/${clientId}`, body)
      return data
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to save exemplar post')
    }
  },
)

/** POST /api/v1/exemplar-post/:clientId/refresh/:socialNetworkTypeId */
export const refreshExemplarPostsForSocial = createAsyncThunk(
  "exemplarPosts/refreshForSocial",
  async ({ clientId, socialNetworkTypeId }, { rejectWithValue }) => {
    try {
      if (!clientId || !socialNetworkTypeId) {
        throw new Error("Client ID and social network type ID are required")
      }
      await post(`exemplar-posts/clients/${clientId}/social-network-types/${socialNetworkTypeId}/refresh`, undefined)
      return { clientId, socialNetworkTypeId }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to refresh exemplar posts")
    }
  },
)

/** DELETE /api/v1/exemplar-post/:postId */
export const deleteExemplarPost = createAsyncThunk(
  'exemplarPosts/delete',
  async (postId, { rejectWithValue }) => {
    try {
      await del(`exemplar-posts/${postId}`)
      return postId
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to delete exemplar post')
    }
  },
)

const initialState = {
  clientId: null,
  list: [],
  status: 'idle',
  error: null,
  saveStatus: 'idle',
  saveError: null,
  deleteStatus: 'idle',
  deleteError: null,
  refreshStatusBySocialType: {},
  refreshError: null,
}

const exemplarPostSlice = createSlice({
  name: 'exemplarPosts',
  initialState,
  reducers: {
    clearExemplarPosts: (state) => {
      state.clientId = null
      state.list = []
      state.status = 'idle'
      state.error = null
    },
    clearExemplarSaveError: (state) => {
      state.saveError = null
      state.saveStatus = 'idle'
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExemplarPostsByClientId.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchExemplarPostsByClientId.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.clientId = action.payload.clientId
        state.list = action.payload.list || []
      })
      .addCase(fetchExemplarPostsByClientId.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
      })
      .addCase(saveExemplarPost.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(saveExemplarPost.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        const row = action.payload
        if (!row || row.id == null) return
        const idx = state.list.findIndex((p) => p.id === row.id)
        if (idx >= 0) state.list[idx] = row
        else state.list.push(row)
      })
      .addCase(saveExemplarPost.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action.payload
      })
      .addCase(deleteExemplarPost.pending, (state) => {
        state.deleteStatus = 'loading'
        state.deleteError = null
      })
      .addCase(deleteExemplarPost.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded'
        state.list = state.list.filter((p) => p.id !== action.payload)
      })
      .addCase(deleteExemplarPost.rejected, (state, action) => {
        state.deleteStatus = 'failed'
        state.deleteError = action.payload
      })
      .addCase(refreshExemplarPostsForSocial.pending, (state, action) => {
        const id = action.meta.arg?.socialNetworkTypeId
        if (id != null) {
          state.refreshStatusBySocialType[String(id)] = "loading"
        }
        state.refreshError = null
      })
      .addCase(refreshExemplarPostsForSocial.fulfilled, (state, action) => {
        const id = action.payload?.socialNetworkTypeId
        if (id != null) {
          state.refreshStatusBySocialType[String(id)] = "succeeded"
        }
      })
      .addCase(refreshExemplarPostsForSocial.rejected, (state, action) => {
        const id = action.meta.arg?.socialNetworkTypeId
        if (id != null) {
          state.refreshStatusBySocialType[String(id)] = "failed"
        }
        state.refreshError = action.payload
      })
  },
})

export const { clearExemplarPosts, clearExemplarSaveError } = exemplarPostSlice.actions
export default exemplarPostSlice.reducer
